import { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { ensureProfile } from '@/lib/ensureProfile'
import GroupCard from '@/components/GroupCard'
import CreateGroupModal from '@/components/CreateGroupModal'
import { useGroupsFeedShowVendorGroupsFlag } from '@/hooks/useGroupsFeedShowVendorGroupsFlag'

function isVendorGroup(group: { group_type?: string | null }): boolean {
  return group.group_type === 'broadcaster' || group.group_type === 'ad-creator'
}

export default function Groups() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const { showVendorGroupsInFeed } = useGroupsFeedShowVendorGroupsFlag()

  const joinGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      if (!user) throw new Error('Not authenticated')
      await ensureProfile(user)
      const { error } = await supabase
        .from('group_members')
        .insert({
          group_id: groupId,
          user_id: user.id,
          role: 'member',
        })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-groups', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['other-groups', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['user-groups', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['user-groups-sidebar', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['available-groups', user?.id] })
      queryClient.invalidateQueries({ queryKey: ['group-stats'] })
    },
    onError: (err: Error) => {
      alert(err.message || 'Failed to join group')
    },
  })

  const [joiningGroupId, setJoiningGroupId] = useState<string | null>(null)

  const handleJoinGroup = (groupId: string) => {
    setJoiningGroupId(groupId)
    joinGroupMutation.mutate(groupId, {
      onSettled: () => setJoiningGroupId(null),
    })
  }

  // Fetch user's groups (optionally exclude vendor groups per feature flag)
  const { data: myGroups } = useQuery({
    queryKey: ['my-groups', user?.id, showVendorGroupsInFeed],
    queryFn: async () => {
      if (!user) return []
      const { data, error } = await supabase
        .from('group_members')
        .select(`
          group_id,
          groups (
            id,
            name,
            description,
            owner_id,
            group_type,
            created_at,
            profiles!groups_owner_id_fkey (
              display_name
            )
          )
        `)
        .eq('user_id', user.id)

      if (error) throw error

      return (data || [])
        .map((m: any) => ({
          ...m.groups,
          owner: m.groups.profiles?.display_name || 'Unknown',
        }))
        .filter((group: any) => {
          if (showVendorGroupsInFeed) return true
          return !group.group_type || !isVendorGroup(group)
        })
    },
    enabled: !!user,
  })

  // Fetch all groups (excluding user's groups; optionally exclude vendor groups per feature flag)
  const { data: otherGroups } = useQuery({
    queryKey: ['other-groups', user?.id, showVendorGroupsInFeed],
    queryFn: async () => {
      if (!user) return []

      const { data: memberships } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', user.id)

      const userGroupIds = memberships?.map((m) => m.group_id) || []

      const { data: allGroups, error } = await supabase
        .from('groups')
        .select(`
          id,
          name,
          description,
          owner_id,
          group_type,
          created_at,
          profiles!groups_owner_id_fkey (
            display_name
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error

      return (allGroups || [])
        .filter((g: any) => {
          if (userGroupIds.includes(g.id)) return false
          if (!showVendorGroupsInFeed && isVendorGroup(g)) return false
          return true
        })
        .map((g: any) => ({
          ...g,
          owner: g.profiles?.display_name || 'Unknown',
        }))
    },
    enabled: !!user,
  })

  // Get statistics for groups
  const { data: groupStats } = useQuery({
    queryKey: ['group-stats', myGroups, otherGroups],
    queryFn: async () => {
      const allGroups = [...(myGroups || []), ...(otherGroups || [])]
      const stats: Record<string, { members: number; comments: number }> = {}

      await Promise.all(
        allGroups.map(async (group: any) => {
          // Get member count
          const { count: memberCount } = await supabase
            .from('group_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', group.id)

          // Get comment count (via threads)
          const { data: threads } = await supabase
            .from('threads')
            .select('id')
            .eq('group_id', group.id)

          const threadIds = threads?.map((t) => t.id) || []
          let commentCount = 0
          if (threadIds.length > 0) {
            const { count } = await supabase
              .from('comments')
              .select('*', { count: 'exact', head: true })
              .in('thread_id', threadIds)
              .eq('is_hidden', false)

            commentCount = count || 0
          }

          stats[group.id] = {
            members: memberCount || 0,
            comments: commentCount,
          }
        })
      )

      return stats
    },
    enabled: !!(myGroups || otherGroups),
  })

  const handleCreateGroup = () => {
    setShowCreateModal(true)
  }

  // Filter groups based on search
  const filterGroups = (groups: any[]) => {
    if (!searchQuery.trim()) return groups
    const query = searchQuery.toLowerCase()
    return groups.filter(
      (group) =>
        group.name.toLowerCase().includes(query) ||
        group.description?.toLowerCase().includes(query)
    )
  }

  const filteredMyGroups = filterGroups(myGroups || [])
  const filteredOtherGroups = filterGroups(otherGroups || [])

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 
          className="text-3xl font-bold mb-4"
          style={{ color: 'var(--text-primary)' }}
        >
          Groups
        </h1>
        
        <div className="flex gap-4 items-center">
          <input
            type="text"
            placeholder="Search groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2 border-2 rounded"
            style={{
              borderColor: 'var(--bg-tertiary)',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
          />
          <button
            onClick={handleCreateGroup}
            className="px-6 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
          >
            Create New Group
          </button>
        </div>
      </div>

      {/* My Groups Section */}
      <div className="mb-8">
        <h2 
          className="text-2xl font-bold mb-4"
          style={{ color: 'var(--text-primary)' }}
        >
          My Groups ({filteredMyGroups.length})
        </h2>
        {filteredMyGroups.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredMyGroups.map((group: any) => (
              <GroupCard
                key={group.id}
                group={group}
                stats={groupStats?.[group.id]}
              />
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--text-primary)' }}>
            You are not a member of any groups yet.
          </p>
        )}
      </div>

      {/* Other Groups Section */}
      <div>
        <h2 
          className="text-2xl font-bold mb-4"
          style={{ color: 'var(--text-primary)' }}
        >
          Other Groups ({filteredOtherGroups.length})
        </h2>
        {filteredOtherGroups.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredOtherGroups.map((group: any) => (
              <GroupCard
                key={group.id}
                group={group}
                stats={groupStats?.[group.id]}
                onJoin={handleJoinGroup}
                isJoining={joiningGroupId === group.id}
              />
            ))}
          </div>
        ) : (
          <p style={{ color: 'var(--text-primary)' }}>
            No other groups available.
          </p>
        )}
      </div>

      {showCreateModal && (
        <CreateGroupModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            // Query will refetch automatically
          }}
        />
      )}
    </div>
  )
}

